import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type { ColumnDef } from '@tanstack/react-table';

import { DataTable } from '@/components/glitch-ui/data-table';
import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import { useAdPerformanceReportQuery, metaCampaignOverviewQueryOptions, metaIntegrationStatusQueryOptions } from '@/lib/query-options';
import { formatMoney, toDisplayAmount } from '@/lib/performance/money';
import { deriveMetrics, formatRate, formatRoas } from '@/lib/performance/metrics';
import { pivotLeaves, availableDimensions, PIVOT_DIMENSIONS, type PivotDimension, type PivotLeaf, type PivotRow } from '@/lib/performance/pivot';
import { dateWindow } from '@/lib/performance/date-window';
import { platformsInScope, type PlatformScope } from '@/lib/performance/types';
import { PlatformLogo } from '@/lib/performance/platform';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { usePerformanceData, useScopedCampaigns } from '@/hooks/usePerformanceData';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ColumnsPopover } from '../cells';
import { CampaignsTab, type CampaignSubView } from '../tabs/campaigns-tab';
import {
  consolidatedPerformanceDateRangeAtom,
  consolidatedPerformanceGroupByAtom,
  consolidatedPerformanceComparisonAtom,
  consolidatedPerformanceCampaignTypesAtom,
  consolidatedPerformanceCampaignIdsAtom,
  consolidatedPerformanceIncludeExternalAtom,
} from '@/store';

// Column-picker metadata for the Campaigns / Ad-Group tables. Duplicated
// from perf-v2 (performance-v2.tsx:103-151) so the Google Campaigns view can
// drive the same column-visibility hook; de-dup tracked in #5994.
const CAMPAIGN_TABLE_COLUMNS: { id: string; label: string; pinned?: boolean }[] = [
  { id: 'name',         label: 'Campaign',     pinned: true },
  { id: 'status',       label: 'Status' },
  { id: 'impressions',  label: 'Impressions' },
  { id: 'clicks',       label: 'Clicks' },
  { id: 'conversions',  label: 'Conversions' },
  { id: 'cost',         label: 'Cost' },
  { id: 'cpa',          label: 'CPA' },
  { id: 'roas',         label: 'ROAS' },
  { id: 'searchIS',     label: 'Search IS' },
  { id: 'lostIS',       label: 'Lost IS' },
  { id: 'optScore',     label: 'Opt. score' },
  { id: 'bidStrategy',  label: 'Bid strategy' },
];
const CAMPAIGN_COLUMN_IDS = new Set(CAMPAIGN_TABLE_COLUMNS.map(c => c.id));
const CAMPAIGN_PINNED_COLUMN_IDS = new Set(CAMPAIGN_TABLE_COLUMNS.filter(c => c.pinned).map(c => c.id));

const AD_GROUP_TABLE_COLUMNS: { id: string; label: string; pinned?: boolean }[] = [
  { id: 'adGroupName', label: 'Ad Group',    pinned: true },
  { id: 'adCount',     label: 'Ads count' },
  { id: 'impressions', label: 'Impressions' },
  { id: 'clicks',      label: 'Clicks' },
  { id: 'ctr',         label: 'CTR' },
  { id: 'conversions', label: 'Conversions' },
  { id: 'cost',        label: 'Cost' },
  { id: 'cpa',         label: 'CPA' },
  { id: 'roas',        label: 'ROAS' },
];
const AD_GROUP_COLUMN_IDS = new Set(AD_GROUP_TABLE_COLUMNS.map(c => c.id));
const AD_GROUP_PINNED_COLUMN_IDS = new Set(AD_GROUP_TABLE_COLUMNS.filter(c => c.pinned).map(c => c.id));

// perf-v2 DEFAULT_LAYOUTS.campaigns tab id under the consolidated shell's
// shared widget-layout atom (see CONSOLIDATED_DEFAULT_LAYOUTS).
const GOOGLE_CAMPAIGNS_LAYOUT_KEY = 'google-campaigns';

// Meta-native campaign attributes not carried on the generic pivot (#6057) — looked up
// by the campaign's pivot key (`meta:<id>`) when grouping by Campaign under Meta scope.
interface MetaCampaignExtras {
  campaignType: string | null;
  frequency: number | null;
  winnerCount: number;
  loserCount: number;
}

function numOrNull(value: string | null | undefined): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Unified Campaigns table (#5971, CP-7; #6040, T2). A dimension pivot: the same
 * ad-level (Google) + campaign-level (Meta) data grouped by a user-chosen
 * dimension (Platform / Campaign / Ad group), rendering the canonical metric block
 * (metrics.ts). Generalizes the old fixed campaign grouping into the shared pivot;
 * more dimensions (Ad, Angle, Location) plug into PIVOT_DIMENSIONS (T3 / #6037).
 */
export function CampaignsPanel({ scope }: { scope: PlatformScope }) {
  const inScope = platformsInScope(scope);
  const googleInScope = inScope.includes('google');
  const metaInScope = inScope.includes('meta');
  const { symbol } = useCurrencySymbol();
  const cs = symbol ?? '';

  // Honor the toolbar date range through the shared exact-day window (#6274).
  // Meta stays snapshot-backed (its own sync window).
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const window = useMemo(() => dateWindow(selectedDays), [selectedDays]);

  // #6277 — the Google half of the merged pivot uses the SAME campaign scoping
  // as Google scope (it was account-wide `undefined`, so flipping All → Google
  // changed the Google numbers and the campaign filters were inert here).
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);
  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);
  const { scopedGoogleAdsIds } = useScopedCampaigns({ includeExternal, typeFilter, selectedCampaignIds });

  // #6278 — Meta money renders in the Meta AD ACCOUNT's currency (the Audience
  // panel's #6182 sourcing); one org can run differently-billed accounts.
  const { data: metaStatus } = useQuery({ ...metaIntegrationStatusQueryOptions(), enabled: metaInScope });
  const { symbol: metaSymbol } = useCurrencySymbol(undefined, metaStatus?.selected_ad_account?.currency);
  const metaCs = metaSymbol ?? cs;
  const symFor = (p: PivotRow['platform']) => (p === 'meta' ? metaCs : cs);


  const { data: adData, isLoading: googleLoading } = useAdPerformanceReportQuery(
    scopedGoogleAdsIds || undefined,
    undefined,
    {
      startDate: window.start_date,
      endDate: window.end_date,
      // Disabled when the filter matches nothing — an absent campaign_ids
      // param means account-wide to the backend (#6275).
      enabled: googleInScope && scopedGoogleAdsIds.length > 0,
    },
  );
  const { data: metaResp, isLoading: metaLoading } = useQuery(metaCampaignOverviewQueryOptions(metaInScope));

  // Group-by dimensions available for this scope; clamp the selection when scope changes.
  // Platform grouping is intentionally omitted — the top-level scope switch (All/Google/
  // Meta) already serves that purpose, so offering it here would clash.
  const dimensions = useMemo(() => availableDimensions(scope, ['campaign', 'ad-group']), [scope]);
  const [groupBy, setGroupBy] = useState<PivotDimension>('campaign');
  const activeDimension = dimensions.includes(groupBy) ? groupBy : (dimensions[0] ?? 'campaign');

  // Flatten both platforms into leaves — Google at ad level so ad-group grouping
  // works; Meta at campaign level (no ad-group, so those leaves drop out of the
  // ad-group pivot) — then group by the active dimension.
  const { leaves, extras } = useMemo(() => {
    const out: PivotLeaf[] = [];
    const ex = new Map<string, MetaCampaignExtras>();
    if (googleInScope && adData) {
      for (const ad of adData) {
        out.push({
          platform: 'google',
          campaignId: ad.campaign_id,
          campaignName: ad.campaign_name || ad.campaign_id,
          adGroupId: ad.ad_group_id,
          adGroupName: ad.ad_group_name || ad.ad_group_id,
          status: null,
          impressions: ad.impressions ?? 0,
          clicks: ad.clicks ?? 0,
          results: ad.conversions ?? 0,
          spend: ad.cost ?? 0,
          conversionValue: ad.all_conversions_value ?? 0,
        });
      }
    }
    if (metaInScope && metaResp) {
      for (const m of metaResp.rows) {
        out.push({
          platform: 'meta',
          campaignId: m.meta_campaign_id,
          campaignName: m.name || m.meta_campaign_id,
          adGroupId: null,
          adGroupName: null,
          status: m.status ?? null,
          impressions: m.impressions,
          clicks: m.clicks,
          results: m.leads,
          spend: toDisplayAmount(m.spend_cents, 'meta'),
          conversionValue: null,
        });
        ex.set(`meta:${m.meta_campaign_id}`, {
          campaignType: m.campaign_type ?? null,
          frequency: numOrNull(m.frequency),
          winnerCount: m.winner_ad_count,
          loserCount: m.loser_ad_count,
        });
      }
    }
    return { leaves: out, extras: ex };
  }, [googleInScope, adData, metaInScope, metaResp]);

  const rows = useMemo(() => pivotLeaves(leaves, activeDimension), [leaves, activeDimension]);

  const columns = useMemo<ColumnDef<PivotRow, unknown>[]>(() => {
    const cols: ColumnDef<PivotRow, unknown>[] = [];
    // Thin, sortable platform-logo column pinned to the left of the name — only under
    // All scope (mixed platforms). Under a single-platform scope the rows are already
    // filtered to one platform, so it would be noise.
    if (scope === 'all') {
      cols.push({
        id: 'platform',
        accessorFn: (r) => r.platform,
        header: '',
        cell: ({ row }) => <PlatformLogo platform={row.original.platform} className="h-4 w-4" />,
        meta: { className: 'pl-4 pr-1 w-8' },
      });
    }
    cols.push({
      accessorKey: 'label',
      header: PIVOT_DIMENSIONS[activeDimension].columnHeader,
      cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.label}</span>,
    });
    cols.push(
      // Layer 2 — volume
      { accessorKey: 'impressions', header: 'Impr.', cell: ({ row }) => row.original.impressions.toLocaleString() },
      { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.clicks.toLocaleString() },
      {
        accessorKey: 'results',
        // Single-platform scope uses that platform's word; the mixed "All" view
        // shows Conversions (Google's term) with a per-row "leads" suffix on Meta rows.
        header: scope === 'meta' ? 'Leads' : 'Conv.',
        cell: ({ row }) =>
          scope === 'meta'
            ? Math.round(row.original.results).toLocaleString()
            : row.original.resultKind === 'leads'
              ? `${Math.round(row.original.results).toLocaleString()} leads`
              : Math.round(row.original.results).toLocaleString(),
      },
      // Layer 1 — money
      { accessorKey: 'spend', header: 'Spend', cell: ({ row }) => formatMoney(row.original.spend, symFor(row.original.platform)) },
      { id: 'roas', accessorFn: (r) => deriveMetrics(r).roas, header: 'ROAS', cell: ({ row }) => formatRoas(deriveMetrics(row.original).roas) },
      { id: 'cpc', accessorFn: (r) => deriveMetrics(r).cpc, header: 'CPC', cell: ({ row }) => formatMoney(deriveMetrics(row.original).cpc, symFor(row.original.platform)) },
      // Layer 3 — rates
      { id: 'ctr', accessorFn: (r) => deriveMetrics(r).ctr, header: 'CTR', cell: ({ row }) => formatRate(deriveMetrics(row.original).ctr, 2) },
      { id: 'imprToConv', accessorFn: (r) => deriveMetrics(r).imprToConv, header: 'Impr→Conv.', cell: ({ row }) => formatRate(deriveMetrics(row.original).imprToConv, 2) },
      { id: 'clickToConv', accessorFn: (r) => deriveMetrics(r).clickToConv, header: 'Conv. rate', cell: ({ row }) => formatRate(deriveMetrics(row.original).clickToConv, 1) },
      // Efficiency
      {
        id: 'cpa',
        accessorFn: (r) => (r.results > 0 ? r.spend / r.results : null),
        header: scope === 'meta' ? 'Cost / lead' : 'Cost / conv.',
        cell: ({ row }) => (row.original.results > 0 ? formatMoney(row.original.spend / row.original.results, symFor(row.original.platform)) : '—'),
      },
    );
    // Status is only meaningful per-campaign (Meta carries it); omit for other pivots.
    if (activeDimension === 'campaign') {
      cols.push({ id: 'status', header: 'Status', cell: ({ row }) => row.original.status ?? '—' });
      // Meta-native campaign attributes (#6057) — only under Meta scope; Google campaigns
      // have no type / frequency / winner-loser verdicts, so they'd be blank under All.
      if (scope === 'meta') {
        cols.push(
          {
            id: 'campaignType',
            header: 'Type',
            cell: ({ row }) => {
              const t = extras.get(row.original.key)?.campaignType;
              return t ? <span className="capitalize text-gray-700">{t.toLowerCase()}</span> : '—';
            },
          },
          {
            id: 'frequency',
            header: 'Freq.',
            cell: ({ row }) => {
              const f = extras.get(row.original.key)?.frequency;
              return f != null ? f.toFixed(2) : '—';
            },
          },
          {
            id: 'verdicts',
            header: 'W / L',
            cell: ({ row }) => {
              const e = extras.get(row.original.key);
              const w = e?.winnerCount ?? 0;
              const l = e?.loserCount ?? 0;
              return w === 0 && l === 0 ? (
                '—'
              ) : (
                <span>
                  <span className="font-medium text-green-600">{w}</span> / <span className="font-medium text-red-600">{l}</span>
                </span>
              );
            },
          },
        );
      }
    }
    return cols;
  }, [cs, metaCs, scope, activeDimension, extras]);

  const loading = (googleInScope && googleLoading) || (metaInScope && metaLoading);
  if (loading) {
    return <div className="flex h-24 items-center justify-center text-sm text-gray-400">Loading…</div>;
  }

  return (
    <div className="space-y-3">
      {dimensions.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Group by</span>
          <Tabs value={activeDimension} onValueChange={(v) => setGroupBy(v as PivotDimension)}>
            <TabsList>
              {dimensions.map((d) => (
                <TabsTrigger key={d} value={d}>
                  {PIVOT_DIMENSIONS[d].label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}
      <DataTable
        data={rows}
        columns={columns}
        pageSize={15}
        emptyState={metaInScope && !googleInScope ? 'No Meta data in the synced window' : 'No data with activity in this period'}
      />
      {/* #6278 — the Meta snapshot endpoints have no date parameters: their figures
          always cover the sync job's trailing 7-day window, unlike the Google half
          which follows the toolbar range. Say so instead of implying one period. */}
      {metaInScope && (
        <p className="text-xs text-gray-500">
          Meta figures cover the last 7 days of synced data{googleInScope ? '; Google figures follow the selected date range' : ''}.
        </p>
      )}
    </div>
  );
}

/**
 * The Google-specific Campaigns view, reusing perf-v2's CampaignsTab verbatim.
 * Fed by the shared usePerformanceData hook (the same data layer /performance-v2
 * uses) so it honors the toolbar filters (#5995) with no duplicated computation.
 * Owns the sub-view toggle + column pickers locally; the Saved Views popover is
 * intentionally omitted for now.
 */
function GoogleCampaignsSection({
  getTabLayout,
  onReorder,
  onResize,
}: {
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
}) {
  const { symbol } = useCurrencySymbol('google');
  const cs = symbol ?? '';

  const [subView, setSubView] = useState<CampaignSubView>('by-type');

  const {
    visible: visibleCampaignColumns, toggle: toggleCampaignColumn, reset: resetCampaignColumns,
  } = useColumnVisibility({ settingsKey: 'campaigns', allIds: CAMPAIGN_COLUMN_IDS, pinnedIds: CAMPAIGN_PINNED_COLUMN_IDS });
  const {
    visible: visibleAdGroupColumns, toggle: toggleAdGroupColumn, reset: resetAdGroupColumns,
  } = useColumnVisibility({ settingsKey: 'ad_groups', allIds: AD_GROUP_COLUMN_IDS, pinnedIds: AD_GROUP_PINNED_COLUMN_IDS });

  // Driven by the shared filter toolbar (#5971/#5995).
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const groupBy = useAtomValue(consolidatedPerformanceGroupByAtom);
  const comparison = useAtomValue(consolidatedPerformanceComparisonAtom);
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);

  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);

  const perf = usePerformanceData({
    includeExternal,
    tab: 'campaigns',
    campaignSubView: subView,
    comparisonMode: comparison,
    selectedCampaignIds,
    chartDateRange: selectedDays,
    chartGroupBy: groupBy,
    showUserEdits: false,
    selectedMetric: 'conversions',
    selectedDays,
    typeFilter,
    cs,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Tabs value={subView} onValueChange={(v) => setSubView(v as CampaignSubView)}>
          <TabsList>
            <TabsTrigger value="by-type">By Type</TabsTrigger>
            <TabsTrigger value="by-campaign">By Campaign</TabsTrigger>
            <TabsTrigger value="by-ad-group">By Ad Group</TabsTrigger>
            <TabsTrigger value="by-conversion-action">By Conv. Action</TabsTrigger>
          </TabsList>
        </Tabs>
        {subView === 'by-campaign' && (
          <ColumnsPopover
            columnsMeta={CAMPAIGN_TABLE_COLUMNS}
            visibleIds={visibleCampaignColumns}
            totalIds={CAMPAIGN_COLUMN_IDS}
            onToggle={toggleCampaignColumn}
            onReset={resetCampaignColumns}
          />
        )}
        {subView === 'by-ad-group' && (
          <ColumnsPopover
            columnsMeta={AD_GROUP_TABLE_COLUMNS}
            visibleIds={visibleAdGroupColumns}
            totalIds={AD_GROUP_COLUMN_IDS}
            onToggle={toggleAdGroupColumn}
            onReset={resetAdGroupColumns}
          />
        )}
      </div>
      <CampaignsTab
        subView={subView}
        typeMetrics={perf.typeMetrics}
        filteredCampaigns={perf.filteredCampaigns}
        adGroupMetrics={perf.adGroupMetrics}
        conversionActions={perf.conversionActionsData}
        isConversionActionsLoading={perf.isConversionActionsLoading}
        campaignStatusData={perf.campaignStatusData}
        campaignStatusByGoogleId={perf.campaignStatusByGoogleId}
        previousCampaignTotalsById={perf.previousCampaignTotalsById}
        activeCampaigns={perf.activeCampaigns}
        visibleCampaignColumns={visibleCampaignColumns}
        visibleAdGroupColumns={visibleAdGroupColumns}
        currencySymbol={cs}
        dateRangeLabel={perf.dateRangeLabel}
        dateRangeFilter={perf.dateRangeFilter}
        layout={getTabLayout(GOOGLE_CAMPAIGNS_LAYOUT_KEY)}
        onReorder={(l) => onReorder(GOOGLE_CAMPAIGNS_LAYOUT_KEY, l)}
        onResize={(id, w) => onResize(GOOGLE_CAMPAIGNS_LAYOUT_KEY, id, w)}
      />
    </div>
  );
}

/**
 * Consolidated Campaigns tab (#5995) — hybrid by scope. Google renders the
 * full-depth perf-v2 CampaignsTab (groupings, diagnostics, column pickers) fed
 * by the shared usePerformanceData hook; Meta / All keep the existing merged
 * cross-platform CampaignsPanel table unchanged.
 */
export function ConsolidatedCampaignsPanel({
  scope,
  getTabLayout,
  onReorder,
  onResize,
}: {
  scope: PlatformScope;
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
}) {
  if (scope === 'google') {
    return <GoogleCampaignsSection getTabLayout={getTabLayout} onReorder={onReorder} onResize={onResize} />;
  }
  return <CampaignsPanel scope={scope} />;
}
